import { useMemo } from "react";

import {
  useSalesV4Context,
  useTableRowFilters,
} from "@features/sales-v4/hooks";
import { type TableState } from "@features/sales-v4/types";
import { selectedTabToLevel } from "@features/sales-v4/utils";
import { SALES_METRICS_REPORT_V4 } from "@gql/sales-page-v4";
import { useNorthbeamQuery } from "@hooks/http";
import { useUserAttributionSettings } from "@hooks/use-user-attribution-settings";
import {
  type GetSalesMetricsReportV4,
  type GetSalesMetricsReportV4Variables,
} from "@north-beam/nb-client-side";
import { type SalesMetricV2 } from "@north-beam/nb-client-side/metrics-v2";
import { type SalesMetricRow } from "@north-beam/nb-common";

interface Props {
  metrics: SalesMetricV2[];
  row: SalesMetricRow;
  tableState: TableState;
}

export const useMetricsData = ({ metrics, row, tableState }: Props) => {
  const { attributionModel, attributionWindow, accountingMode } =
    useUserAttributionSettings();
  const { dateRangeInterval } = useSalesV4Context();

  const {
    dimensionIds,
    metricFilters,
    breakdownFilters,
    campaignFilters,
    adsetFilters,
    adFilters,
    advancedSearch,
    isSummary,
  } = useTableRowFilters({
    tableState,
    row,
  });

  const { data: rawData, loading } = useNorthbeamQuery<
    GetSalesMetricsReportV4,
    GetSalesMetricsReportV4Variables
  >(SALES_METRICS_REPORT_V4, {
    variables: {
      attributionModel,
      attributionWindow,
      accountingMode,
      dateRange: dateRangeInterval,
      level: selectedTabToLevel(tableState.selectedTab),
      timeGranularity: "daily",
      metricIds: metrics.map((e) => e.id),
      dimensionIds,
      metricFilters,
      advancedSearch,
      breakdownFilters,
      campaignFilters,
      adsetFilters,
      adFilters,
      isSummary,
    },
  });

  const data = useMemo(() => {
    if (!rawData) {
      return null;
    }
    return rawData.me.salesMetricsReportV4.actual[0] as SalesMetricRow;
  }, [rawData]);

  return { data, loading };
};
